import { useCallback, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

export function usePrompt(
  message: string,
  when = true
): {
  unblock: () => void;
} {
  const location = useLocation();

  // Block browser/tab close with beforeunload
  useEffect(() => {
    if (!when) {
      return undefined;
    }

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [when]);

  // Block in-app navigation by intercepting popstate
  useEffect(() => {
    if (!when) {
      return undefined;
    }

    // Push a duplicate entry so we can intercept the back button
    window.history.pushState(null, '', window.location.href);

    const handlePopState = () => {
      if (window.confirm(message)) {
        // User confirmed — go back for real
        window.history.back();
      } else {
        // User cancelled — re-push to keep them on the page
        window.history.pushState(null, '', window.location.href);
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [when, message, location]);

  const unblock = useCallback(() => {
    // No-op: callers that need to programmatically unblock
    // can just set the `when` condition to false before navigating
  }, []);

  return { unblock };
}
